export const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    clearInterval(timer);
    const context = this;
    timer = setTimeout(() => {
      fn.apply(context, args);
    }, delay);
  };
};

export const handleResize = () => {
  const threshold = 600;
  const initialDiff = window.innerWidth > threshold ? 1 : -1;

  window.addEventListener("resize", () => {
    const width = window.innerWidth;
    const currentDiff = width - threshold;
    if (currentDiff * initialDiff < 0) {
      // eslint-disable-next-line no-restricted-globals, no-undef
      location.reload();
    }
  });
};

export function customTypingEffect(wordsList, id, speed = 200) {
  let charPos = 0;
  let wordPos = 0;
  let type = "inc";
  const wordLen = wordsList.length;
  const demoEle = document.getElementById(id);

  return function typeWriter() {
    if (!demoEle) {
      return;
    }
    const txt = wordsList[wordPos];
    const txtLen = txt ? txt.length : 0;
    // console.log(`wordPos, charPos: `, wordPos, charPos);
    if (type === "inc") {
      if (charPos < txtLen) {
        demoEle.innerHTML += txt.charAt(charPos);
        charPos += 1;
      } else {
        type = "dec";
      }
    } else if (type === "dec") {
      if (charPos > -1) {
        demoEle.innerHTML = txt.substr(0, charPos);
        charPos -= 1;
      } else {
        type = "inc";
        wordPos = (wordPos + 1) % wordLen;
      }
    }
    setTimeout(typeWriter, speed);
  };
}

export function scrollToTop({ isSmooth = true }) {
  const app = document.getElementById("app");
  app.scrollTo({ top: 0, behavior: isSmooth ? "smooth" : "auto" });
}

/**
 * Calculates the total years of experience from the given start date to the current date.
 * The result is returned as a string in years with one decimal point precision.
 *
 * @param {string} startDateStr Start date in "YYYY-MM-DD" format.
 * @returns {string} Total years of experience as a string in decimal format.
 */
export function calculateYearsOfExperience(startDateStr) {
  const startDate = new Date(startDateStr);
  const startMonth = startDate.getFullYear() * 12 + startDate.getMonth();
  const currentDate = new Date();
  const endMonth = currentDate.getFullYear() * 12 + currentDate.getMonth();
  const monthInterval = endMonth - startMonth;

  const yearsOfExperience = Math.floor(monthInterval / 12);
  const monthsOfExperience = monthInterval % 12;
  const monthsInDecimal = (monthsOfExperience / 12).toFixed(1);
  if (Number.isNaN(yearsOfExperience) || Number.isNaN(monthsOfExperience)) {
    return "0";
  }
  return `${Number(yearsOfExperience) + Number(monthsInDecimal)}`;
}

/**
 * Calculates the difference in years and months between the given start date and the current date.
 * @param {string} startDateStr Start date in "YYYY-MM-DD" format.
 * @returns {{years: number, months: number}} Difference in years and months between the given start and current dates.
 */
export function calculateYearsAndMonthsOfExperience(startDateStr) {
  const result = { years: 0, months: 0 };
  const startDate = new Date(startDateStr);
  const startMonth = startDate.getFullYear() * 12 + startDate.getMonth();
  const currentDate = new Date();
  const endMonth = currentDate.getFullYear() * 12 + currentDate.getMonth();
  const monthInterval = endMonth - startMonth;

  const yearsOfExperience = Math.floor(monthInterval / 12);
  const monthsOfExperience = monthInterval % 12;
  if (Number.isNaN(yearsOfExperience) || Number.isNaN(monthsOfExperience)) {
    return result;
  }
  result.years = Number(yearsOfExperience);
  result.months = Number(monthsOfExperience);
  return result;
}

/**
 * Calculates the difference in months between the given start and end dates.
 * @param {string} startDateStr Start date in "YYYY-MM-DD" or "YYYY-MM"  format.
 * @param {string} endDateStr End date in "YYYY-MM-DD" or "YYYY-MM" format.
 * @returns {number} Difference in months between the given start and end dates.
 */
export function calculateDiffInMonths(startDateStr, endDateStr) {
  const startDate = new Date(startDateStr);
  const startMonth = startDate.getFullYear() * 12 + startDate.getMonth();
  const endDate = new Date(endDateStr);
  const endMonth = endDate.getFullYear() * 12 + endDate.getMonth();
  const monthInterval = endMonth - startMonth;
  return monthInterval;
}

/**
 * Converts given number of months into years and months in decimal.
 * @param {number} months Number of months to convert.
 * @returns {number} Number of years and months in decimal (e.g. 1.5 for 1 year and 6 months).
 */
export function convertMonthsToYears(months) {
  const years = Math.floor(months / 12);
  const monthsInDecimal = ((months % 12) / 12).toFixed(1);
  return Number(`${Number(years) + Number(monthsInDecimal)}`);
}

// Filter list in the same order as given in the 'list'
export function filterInSameOrder(parentList, filterIds) {
  return filterIds
    .map((filterId) => parentList.find((item) => item.id === filterId))
    .filter(Boolean);
}

// Calculate duration in years and months from start date to end date
export function calculateDuration(startDate, endDate) {
  const parseDate = (dateStr) => {
    if (dateStr === "Present") return new Date();
    const [month, year] = dateStr.split(" ");
    return new Date(`${month} 1, ${year}`);
  };

  const start = parseDate(startDate);
  const end = parseDate(endDate);

  const totalMonths =
    (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  const yearStr = years > 0 ? `${years} ${years === 1 ? "year" : "years"}` : "";
  const monthStr = months > 0 ? `${months} ${months === 1 ? "month" : "months"}` : "";

  return [yearStr, monthStr].filter(Boolean).join(" ");
}

// Generate a simple id from a string: lowercase, spaces -> underscore
export function generateId(str) {
  if (!str) return "";
  return String(str).trim().toLowerCase().replace(/\s+/g, "_");
}

/**
 * Parses a human-readable issue date string into a Date object.
 *
 * Supported formats:
 * - "May 2023"     (month + year)
 * - "2020"         (year only)
 * - Any value valid for the Date constructor as a fallback.
 *
 * Returns a Date at epoch (new Date(0)) when the input is missing/invalid,
 * so it safely sorts to the oldest end of a list.
 *
 * @param {string} value Raw issueDate value from config.
 * @returns {Date} Parsed Date instance.
 */
export function parseIssueDate(value) {
  if (!value) return new Date(0);
  const str = String(value).trim();

  // Handle formats like "May 2023"
  const parts = str.split(" ");
  if (parts.length === 2) {
    const [month, year] = parts;
    return new Date(`${month} 1, ${year}`);
  }

  // Handle plain 4-digit year like "2020"
  if (/^\d{4}$/.test(str)) {
    return new Date(`${str}-01-01`);
  }

  // Fallback: let Date try to parse it
  const parsed = new Date(str);
  // If invalid, return epoch to avoid NaN in sort
  if (Number.isNaN(parsed.getTime())) {
    return new Date(0);
  }
  return parsed;
}
